import store from 'infra/redux/store'
import { apiClient } from 'infra/api'
import * as sls from 'infra/redux/reducers/slices/slices'
import { startPolling } from 'infra/utils'
import { delay } from 'Utils/Helpers'

// Constant which holds slice name -> slice endpoint
export const SLICENAME_TO_ENDPOINT_MAP = {}
// Constant which holds slice endpoint -> slice name
export const ENDPOINT_TO_SLICENAME_MAP = {}
export const SLICENAME_TO_APIFN_THUNK_MAP = {}

/**
 * Parse all thunks and slices names.
 */
for (const property in sls) {
  const { name, endpoint, thunk, apiFn } = sls[property]
  SLICENAME_TO_ENDPOINT_MAP[name] = endpoint
  ENDPOINT_TO_SLICENAME_MAP[endpoint] = name
  if (apiFn) SLICENAME_TO_APIFN_THUNK_MAP[name] = thunk
}

/**
 *
 * @param {import("types").Endpoints} endpoint
 */
export const reduxApiClient = (endpoint) => {
  const client = apiClient(endpoint)
  const dispatch = store.dispatch

  const fetchActions = {
    upsert(data) {
      if (data == null) return
      const sliceName = ENDPOINT_TO_SLICENAME_MAP[endpoint]
      dispatch({
        type: `UPDATE_SLICE_MAP/${sliceName}`,
        payload: data
      })
    },
    updateAll(data) {
      if (data == null) return
      const sliceName = ENDPOINT_TO_SLICENAME_MAP[endpoint]
      dispatch({
        type: `UPDATE_SLICE_MAP/ALL/${sliceName}`,
        payload: data
      })
    },
    delete(data) {
      const sliceName = ENDPOINT_TO_SLICENAME_MAP[endpoint]
      dispatch({
        type: `DELETE/${sliceName}`,
        payload: data
      })
    },
    failed() {
      const sliceName = ENDPOINT_TO_SLICENAME_MAP[endpoint]
      dispatch({ type: `FETCH_FAILED/${sliceName}` })
    }
  }

  return {
    /**
     *
     * @param {import('axios').AxiosRequestConfig['params']} params
     */
    async getByDataParams(params) {
      const data = await client.getByParams(params)
      fetchActions.upsert(data)
      return data
    },
    /**
     * A failed read keeps the slice, marks it errored and resolves like apiClient.getAll.
     * @param {import('infra/api').GetAllApiOptions} options
     * @returns {Promise.<any[]>}
     */
    async getAll(options) {
      // Cursor-paginated endpoints deliver each page to Redux as it arrives
      // (upsert), then the final updateAll performs the stale-key cleanup —
      // non-paginated endpoints are unaffected (onPage never fires).
      const onPage = (rows) => fetchActions.upsert(rows)
      let data
      try {
        data = await client.getAllShared({ ...options, onPage })
      } catch (error) {
        fetchActions.failed()
        return error?.rows || []
      }
      fetchActions.updateAll(data)
      return data
    },
    /**
     * Replaces the slice only after every page loaded; any failed request rejects and leaves it as is.
     * @param {import('infra/api').GetAllApiOptions} options
     * @returns {Promise.<any[]>}
     */
    async getAllOrThrow(options) {
      const data = await client.getAllOrThrow(options)
      fetchActions.updateAll(data)
      return data
    },
    async create(obj) {
      const data = await client.create(obj)
      fetchActions.upsert(data)
      return data
    },
    async createOrThrow(obj) {
      const data = await client.createOrThrow(obj)
      fetchActions.upsert(data)
      return data
    },
    async update(obj) {
      const data = await client.update(obj)
      fetchActions.upsert(data)
      return data
    },
    async updateOrThrow(obj) {
      const data = await client.updateOrThrow(obj)
      fetchActions.upsert(data)
      return data
    },
    async delete(obj) {
      await client.delete(obj)
      fetchActions.delete(obj)
      return obj
    },
    async deleteOrThrow(obj) {
      await client.deleteOrThrow(obj)
      fetchActions.delete(obj)
      return obj
    },
    /**
     * The function starts network polling for the specified object resource, until `checkFn` returns true or `stopPollingFn` is called.
     * @param {string|object} object The object/string to poll
     * @param {((any) => boolean)} testFn The function which will be called with response and must return true or false
     * @param {string=} key
     */
    startRsrcObjectPolling(object, testFn, key) {
      if (!object) return
      let objectName = typeof object === 'string' ? object : object.ObjectMeta.Name

      // If key is present then, check if the polling with that key is already running.
      if (key) {
        if (POLLING_KEYS.includes(key)) return
        // Push to polling keys
        POLLING_KEYS.push(key)
      }
      const apiFn = async () => {
        const obj = await client.getByParams({
          'ObjectMeta.Name': objectName
        })
        await delay(3000)
        return obj?.[0]
      }

      const doFn = (object) => {
        // Remove the polling keys once polling is completed
        POLLING_KEYS = POLLING_KEYS.filter((e) => e !== key)
        fetchActions.upsert(object)
      }

      startPolling(apiFn, testFn, doFn)
    },
    async getAllByOffset(startIndex, endIndex) {
      const data = await client.getAllByOffset(startIndex, endIndex);
      fetchActions.updateAll(data);
      return data;
    }
  }
}

// Variable to store currently running polling
let POLLING_KEYS = []
